/**
 * WizardShell
 *
 * Full-screen overlay layout for the onboarding wizard.
 * Position fixed; inset 0; z-index 50.
 *
 * LEFT SIDEBAR (~280px): WizardSidebar with step indicators
 * MAIN CONTENT:
 *   - WizardTopBar (title + Skip + Reset Dev)
 *   - Scrollable content area (Welcome / Steps 1-7 / Completion)
 *   - WizardBottomBar is rendered INSIDE each step component (steps 1-7 only)
 *
 * Step flow: 0=Welcome → 1-7=steps → 8=Completion
 *
 * Step 1: Company Setup        (mandatory gate)
 * Step 2: User Access          (optional)
 * Step 3: Timesheet Settings   (submissions + time entry tabs only)
 * Step 4: Policy Rules         (working-hours tab only)
 * Step 5: Notifications        (NotificationsPage)
 * Step 6: Approval Workflows   (canManageApprovalWorkflows gate)
 * Step 7: Overtime Types       (optional, requires company)
 *
 * Approval Workflows (step 6) is conditionally included based on canManageApprovalWorkflows().
 * Notifications (step 5) is conditionally included based on canManageNotifications().
 */

import { useState, useCallback } from 'react';
import { useOnboarding, type OnboardingStep } from '@/contexts/OnboardingContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { WizardSidebar, type StepDefinition } from '@/components/onboarding/WizardSidebar';
import { WizardTopBar } from '@/components/onboarding/WizardTopBar';
import { WelcomeScreen } from '@/components/onboarding/WelcomeScreen';
import { CompletionScreen } from '@/components/onboarding/CompletionScreen';
import { Step1CompanySetup } from '@/components/onboarding/steps/Step1CompanySetup';
import { Step2UserAccess } from '@/components/onboarding/steps/Step2UserAccess';
import { Step3TimesheetSettings } from '@/components/onboarding/steps/Step3TimesheetSettings';
import { Step4PolicyRules } from '@/components/onboarding/steps/Step4PolicyRules';
import { Step5Notifications } from '@/components/onboarding/steps/Step5Notifications';
import { Step6ApprovalWorkflows } from '@/components/onboarding/steps/Step6ApprovalWorkflows';
import { Step7OvertimeTypes } from '@/components/onboarding/steps/Step7OvertimeTypes';

type StepStatus = 'completed' | 'skipped' | 'not-configured';

interface StepStatuses {
  step2: StepStatus;
  step3: StepStatus;
  step4: StepStatus;
  step5: StepStatus;
  step6: StepStatus;
  step7: StepStatus;
}

function getTopBarTitle(step: OnboardingStep, totalSteps: number): string {
  switch (step) {
    case 0:
      return 'Welcome to Access Time';
    case 1:
      return `Step 1 of ${totalSteps}`;
    case 2:
      return `Step 2 of ${totalSteps}`;
    case 3:
      return `Step 3 of ${totalSteps}`;
    case 4:
      return `Step 4 of ${totalSteps}`;
    case 5:
      return `Step 5 of ${totalSteps}`;
    case 6:
      return `Step 6 of ${totalSteps}`;
    case 7:
      return `Step 7 of ${totalSteps}`;
    case 8:
      return 'Setup Complete';
    default:
      return 'Access Time Onboarding';
  }
}

export function WizardShell() {
  const { savedStep, saveStep } = useOnboarding();
  const { canManageApprovalWorkflows, canManageNotifications } = usePermissions();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>(savedStep);
  const [stepStatuses, setStepStatuses] = useState<StepStatuses>({
    step2: 'not-configured',
    step3: 'not-configured',
    step4: 'not-configured',
    step5: 'not-configured',
    step6: 'not-configured',
    step7: 'not-configured',
  });
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const showWorkflowStep = canManageApprovalWorkflows();
  const showNotificationsStep = canManageNotifications();

  // Build the full step definitions list — displayNumber is sequential (1–N)
  // based on which steps are actually visible
  const allStepDefs: Omit<StepDefinition, 'displayNumber'>[] = [
    { number: 1, label: 'Company Setup',        description: 'Add your companies' },
    { number: 2, label: 'User Access',           description: 'Assign company access' },
    { number: 3, label: 'Timesheet Settings',    description: 'Set deadlines & entry rules' },
    { number: 4, label: 'Policy Rules',          description: 'Configure policy enforcement' },
    ...(showNotificationsStep
      ? [{ number: 5, label: 'Notifications',    description: 'Set up reminders & alerts' }]
      : []),
    ...(showWorkflowStep
      ? [{ number: 6, label: 'Approval Workflows', description: 'Define approval chains' }]
      : []),
    { number: 7, label: 'Overtime Types',        description: 'Create overtime categories' },
  ] as Omit<StepDefinition, 'displayNumber'>[];

  const visibleStepDefs: StepDefinition[] = allStepDefs.map((s, idx) => ({
    ...s,
    number: s.number as OnboardingStep,
    displayNumber: idx + 1,
  }));

  const totalSteps = visibleStepDefs.length;

  const goToStep = useCallback(
    (step: OnboardingStep) => {
      setCurrentStep(step);
      saveStep(step);
    },
    [saveStep],
  );

  const markStepCompleted = (stepNumber: number) => {
    setCompletedSteps((prev) => new Set([...prev, stepNumber]));
  };

  const isStepCompleted = (stepNumber: number) => completedSteps.has(stepNumber);

  // ── Navigation handlers ────────────────────────────────────────────────────

  const handleWelcomeStart = () => goToStep(1);

  // Step 1
  const handleStep1Next = () => {
    markStepCompleted(1);
    goToStep(2);
  };

  // Step 2
  const handleStep2Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step2: status }));
    if (status === 'completed') markStepCompleted(2);
    goToStep(3);
  };
  const handleStep2Back = () => goToStep(1);

  // Step 3
  const handleStep3Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step3: status }));
    if (status === 'completed') markStepCompleted(3);
    goToStep(4);
  };
  const handleStep3Back = () => goToStep(2);

  // Step 4
  const handleStep4Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step4: status }));
    if (status === 'completed') markStepCompleted(4);
    // Go to notifications if visible, else approvals if visible, else overtime
    if (showNotificationsStep) goToStep(5);
    else if (showWorkflowStep) goToStep(6);
    else goToStep(7);
  };
  const handleStep4Back = () => goToStep(3);

  // Step 5 (Notifications)
  const handleStep5Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step5: status }));
    if (status === 'completed') markStepCompleted(5);
    if (showWorkflowStep) goToStep(6);
    else goToStep(7);
  };
  const handleStep5Back = () => goToStep(4);

  // Step 6 (Approval Workflows)
  const handleStep6Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step6: status }));
    if (status === 'completed') markStepCompleted(6);
    goToStep(7);
  };
  const handleStep6Back = () => {
    if (showNotificationsStep) goToStep(5);
    else goToStep(4);
  };

  // Step 7 (Overtime Types)
  const handleStep7Next = (status: StepStatus) => {
    setStepStatuses((prev) => ({ ...prev, step7: status }));
    if (status === 'completed') markStepCompleted(7);
    goToStep(8);
  };
  const handleStep7Back = () => {
    if (showWorkflowStep) goToStep(6);
    else if (showNotificationsStep) goToStep(5);
    else goToStep(4);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeScreen onStart={handleWelcomeStart} />;
      case 1:
        return <Step1CompanySetup onNext={handleStep1Next} />;
      case 2:
        return <Step2UserAccess onNext={handleStep2Next} onBack={handleStep2Back} />;
      case 3:
        return <Step3TimesheetSettings onNext={handleStep3Next} onBack={handleStep3Back} />;
      case 4:
        return <Step4PolicyRules onNext={handleStep4Next} onBack={handleStep4Back} />;
      case 5:
        if (!showNotificationsStep) {
          goToStep(showWorkflowStep ? 6 : 7);
          return null;
        }
        return <Step5Notifications onNext={handleStep5Next} onBack={handleStep5Back} />;
      case 6:
        if (!showWorkflowStep) {
          goToStep(7);
          return null;
        }
        return <Step6ApprovalWorkflows onNext={handleStep6Next} onBack={handleStep6Back} />;
      case 7:
        return <Step7OvertimeTypes onNext={handleStep7Next} onBack={handleStep7Back} />;
      case 8:
        return <CompletionScreen stepStatuses={stepStatuses} />;
      default:
        return <WelcomeScreen onStart={handleWelcomeStart} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex bg-white">
      {/* Left Sidebar */}
      <WizardSidebar
        currentStep={currentStep}
        steps={visibleStepDefs}
        isStepCompleted={isStepCompleted}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <WizardTopBar
          currentStep={currentStep}
          title={getTopBarTitle(currentStep, totalSteps)}
        />
        <div className="flex-1 overflow-hidden flex flex-col">{renderContent()}</div>
      </div>
    </div>
  );
}
